/**
 * Shared class treatments for the care-plan view controls, so the floating tabs (over the board) and
 * the timeline header's view toggle read as the same control family. Active matches the main nav's
 * selected state (bg-sidebar-accent).
 */

/** The raised "pill" wrapper shared by every floating control (the tab bar, the toggle and sync pills,
 * the add + cart FABs). The hairline border lifts it off the board content behind — `bg-sidebar` alone
 * sits too close to the board in dark mode and bleeds into it. */
export const CARE_PLAN_CONTROL_PILL_CLASS = 'rounded-lg border border-border bg-sidebar p-[3px] shadow-lg';

/**
 * The canvas the plan view sits on: the board, the table, and the strip the timeline card floats in.
 * One surface under all three, so the floating controls and the timeline read as raised off the plan
 * rather than as boxes of their own.
 */
export const CARE_PLAN_CANVAS_CLASS = 'bg-muted/50';

/**
 * Opaque twin, for surfaces content scrolls under — the table's sticky header and its pinned Topic
 * cell. `bg-background` under the same overlay the canvas carries lands on the canvas colour, in
 * either theme.
 */
export const CARE_PLAN_CANVAS_OPAQUE_CLASS =
  "bg-background isolate after:absolute after:inset-0 after:-z-10 after:bg-muted/50 after:content-['']";

/** A count on a control in the bar: primary, since it always means something waiting on the clinician. */
export const CARE_PLAN_CONTROL_COUNT_BADGE_CLASS =
  'absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground tabular-nums';

/** Groups inside the bar's one surface. Five separate pills gave the eye five competing borders. */
export const CARE_PLAN_CONTROL_DIVIDER_CLASS = 'mx-1 h-5 w-px shrink-0 bg-border';

/** An icon-only view tab. `size-8` in the pill's `p-[3px]` makes 40px — the chart rail's tab strip. */
export const CARE_PLAN_TAB_CLASS = 'inline-flex size-8 items-center justify-center rounded-md transition-colors';
export const CARE_PLAN_TAB_ACTIVE_CLASS = 'bg-sidebar-accent text-sidebar-accent-foreground';
export const CARE_PLAN_TAB_INACTIVE_CLASS = 'text-muted-foreground hover:text-foreground';

/** Icon-only toggles — show-details, the timeline collapse toggle, and the timeline's view switch —
 * share the tab's active/inactive treatment. */
export const CARE_PLAN_TOGGLE_ACTIVE_CLASS = 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent';
export const CARE_PLAN_TOGGLE_INACTIVE_CLASS =
  'bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground';

/**
 * Room for the floating bar, so a view's last row can clear it (#9034). 64px clears the bar's top edge
 * at 56px (`p-[3px]` + border around an `h-8` control, `bottom-4` up).
 *
 * Goes on the scroller, not the pane — the pane positions the show-details toggle and the suggestions
 * dock. Padding, not margin: a margin cuts content on a line instead of letting it scroll under the bar.
 *
 * Encounters and lab orders are the exception: `DetailColumn` gives those two a header and its own inner
 * scroller, so the padding shortens that instead of adding scroll room, and their content stops above
 * the bar rather than passing under it.
 */
export const CONTROL_BAR_CLEARANCE_CLASS = 'pb-16';
